import React, { useEffect, useRef, useState } from 'react';
import {
  Mail, Send, FileText, Settings, BarChart3,
  Users, Calendar, Home, ChevronRight, UserCheck, ChevronDown,
  DollarSign, CreditCard, Receipt, ClipboardList, FolderOpen, Shield,
  Ticket, ScanLine, Wallet, PanelLeftClose, PanelLeftOpen, X,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { CURRENT_REUNION_SLUG, REUNION } from '../../config/organization';
import { LG_BREAKPOINT_QUERY } from '../../utils/breakpoints';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface AdminSidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

interface NavSection {
  title: string;
  items: NavItem[];
  roles?: ('admin' | 'organizer' | 'finance_manager')[];
}

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
  roles?: ('admin' | 'organizer' | 'finance_manager')[];
}

const SECTIONS_STORAGE_KEY = 'admin-sidebar-sections-collapsed';

// Hoisted out of the component so the sidebar doesn't re-allocate ~40 NavItems
// on every render (the sidebar re-renders on every route change).
const NAV_SECTIONS: NavSection[] = [
  {
    title: 'User Management',
    roles: ['admin', 'organizer'],
    items: [
      { label: 'Pending Registrations', href: '/admin/pending-users', icon: UserCheck, description: 'Review & verify new accounts', roles: ['admin'] },
      { label: 'Users', href: '/admin/users', icon: Users, description: 'Manage user accounts' },
      { label: 'Members', href: '/admin/members', icon: Users, description: 'Manage family members' },
      { label: 'Edit Audit Log', href: '/admin/audit-log', icon: ClipboardList, description: 'View member edit history', roles: ['admin'] },
      { label: 'Tree Admins', href: '/admin/tree-admins', icon: Shield, description: 'Manage family tree editors', roles: ['admin'] },
    ],
  },
  {
    title: 'Announcements',
    items: [
      { label: 'Announcements', href: '/admin/announcements', icon: Calendar, description: 'Manage announcements' },
    ],
  },
  {
    title: 'Document Management',
    items: [
      { label: 'Documents', href: '/admin/documents', icon: FolderOpen, description: 'Manage document library' },
    ],
  },
  {
    title: 'Financial Management',
    items: [
      { label: 'Payment Dashboard', href: '/admin/payments', icon: DollarSign, description: 'Monitor payments & transactions', roles: ['admin', 'finance_manager'] },
      { label: 'Dues Management', href: '/admin/dues', icon: Calendar, description: 'Manage annual dues & assessments', roles: ['admin', 'finance_manager'] },
      { label: 'Donation Management', href: '/admin/payments/donations', icon: CreditCard, description: 'Manage donation campaigns', roles: ['admin', 'finance_manager'] },
      { label: 'Expenses', href: '/admin/expenses', icon: Wallet, description: 'Track money spent on the reunion', roles: ['admin', 'finance_manager'] },
      { label: 'Financial Summary', href: '/admin/payments/reports', icon: Receipt, description: 'Income, expenses, and CSV export', roles: ['admin', 'finance_manager'] },
    ],
    roles: ['admin', 'finance_manager'],
  },
  {
    title: 'Event Management',
    items: [
      { label: 'All Events', href: '/admin/events', icon: Calendar, description: 'View and manage all events' },
      { label: REUNION.title, href: `/admin/events/${CURRENT_REUNION_SLUG}/edit`, icon: Calendar, description: 'Manage reunion event & RSVPs' },
      { label: 'Items & Sales', href: '/admin/ticketing', icon: Ticket, description: 'Manage sellable items and orders' },
      { label: 'Event Check-In', href: '/admin/ticketing/check-in', icon: ScanLine, description: 'Check in tickets at events' },
    ],
    roles: ['admin', 'organizer'],
  },
  {
    title: 'Communication System',
    items: [
      { label: 'Dashboard', href: '/admin/communications', icon: Mail, description: 'View all communications' },
      { label: 'Compose Email', href: '/admin/communications/compose', icon: Send, description: 'Send messages to family' },
      { label: 'Templates', href: '/admin/communications/templates', icon: FileText, description: 'Manage email templates' },
      { label: 'Analytics', href: '/admin/communications/analytics', icon: BarChart3, description: 'View metrics & reports' },
    ],
  },
];

const DEFAULT_SECTION_COLLAPSED: Record<string, boolean> = {
  'User Management': true,
  'Announcements': true,
  'Document Management': true,
  'Event Management': true,
  'Financial Management': true,
  'Communication System': true,
};

const AdminSidebar: React.FC<AdminSidebarProps> = ({ collapsed, onToggleCollapsed }) => {
  const location = useLocation();
  const { user } = useAuthStore();

  const getInitialCollapsedState = (): Record<string, boolean> => {
    try {
      const saved = localStorage.getItem(SECTIONS_STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_SECTION_COLLAPSED, ...JSON.parse(saved) };
      }
    } catch {
      // ignore parse errors
    }
    return DEFAULT_SECTION_COLLAPSED;
  };

  const [sectionCollapsed, setSectionCollapsed] = useState<Record<string, boolean>>(
    getInitialCollapsedState
  );

  // Mobile drawer state. On lg+ the sidebar is always visible; on smaller
  // viewports it slides in from the left when the FAB is pressed.
  const [mobileOpen, setMobileOpen] = useState(false);

  // Track viewport so the slim/collapsed UI only renders on lg+; on mobile
  // the drawer always shows the full expanded UI for legibility. Defaults to
  // `true` (desktop) so SSR/pre-mount renders match the desktop-default
  // markup; the real value is read after mount.
  const isLg = useMediaQuery(LG_BREAKPOINT_QUERY, true);

  // Close the mobile drawer whenever the route changes so navigating to a
  // page dismisses the overlay.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // When the viewport grows past lg the drawer state is meaningless — clear
  // it so a user who opened the drawer then resized (or rotated their iPad)
  // doesn't leave stale state behind that re-appears on the next resize down.
  useEffect(() => {
    if (isLg && mobileOpen) setMobileOpen(false);
  }, [isLg, mobileOpen]);

  // Drawer-open side effects: Escape to close, body scroll lock, focus
  // management. Mirrors the shared `Modal` component's pattern.
  const asideRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!mobileOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);

    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    asideRef.current?.focus();

    return () => {
      window.removeEventListener('keydown', onKey);
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      previousFocusRef.current?.focus?.();
    };
  }, [mobileOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(sectionCollapsed));
    } catch {
      // ignore storage errors
    }
  }, [sectionCollapsed]);

  const toggleSection = (sectionTitle: string) => {
    setSectionCollapsed((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }));
  };

  const isActivePath = (href: string) => location.pathname === href;

  const hasRoleAccess = (roles?: ('admin' | 'organizer' | 'finance_manager')[]) => {
    if (!roles || roles.length === 0) return true;
    return user && roles.includes(user.role as 'admin' | 'organizer' | 'finance_manager');
  };

  if (!user || !hasRoleAccess(['admin', 'organizer', 'finance_manager'])) {
    return null;
  }

  // On mobile the drawer always renders in expanded mode for legibility.
  // The slim/collapsed UI only applies on lg+ when the user has chosen it.
  const renderCollapsed = isLg && collapsed;

  // Mobile is always w-64; on lg+ width follows the collapse state.
  const desktopWidthClass = collapsed ? 'lg:w-16' : 'lg:w-64';
  const translateClass = mobileOpen
    ? 'translate-x-0'
    : '-translate-x-full lg:translate-x-0';

  // Mobile drawer semantics: the aside is a modal dialog only on mobile.
  // Desktop sees a persistent rail, so the dialog role/modal flag don't apply.
  const isMobileViewport = !isLg;
  const isMobileDrawerOpen = isMobileViewport && mobileOpen;
  const isMobileDrawerClosed = isMobileViewport && !mobileOpen;

  return (
    <>
      {/* Mobile-only backdrop. Tapping it dismisses the drawer. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        ref={asideRef}
        id="admin-sidebar"
        tabIndex={-1}
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white z-40 border-r border-border flex flex-col transition-[width,transform] duration-200 w-64 ${desktopWidthClass} ${translateClass} focus:outline-none`}
        aria-label="Admin navigation"
        role={isMobileViewport ? 'dialog' : undefined}
        aria-modal={isMobileDrawerOpen ? true : undefined}
        aria-hidden={isMobileDrawerClosed}
      >
        {/* Fixed header — stays pinned while the nav below scrolls. */}
        <div
          className={`p-3 border-b border-border flex items-center flex-shrink-0 ${
            renderCollapsed ? 'justify-center' : 'justify-between'
          }`}
        >
          {!renderCollapsed && (
            <div className="flex items-center gap-2 min-w-0">
              <Settings className="w-4 h-4 text-text-tertiary flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="font-semibold text-sm text-text-primary truncate">Admin Panel</h2>
                <p className="text-xs text-text-tertiary capitalize truncate">{user.role}</p>
              </div>
            </div>
          )}
          {/* Desktop collapse toggle */}
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="hidden lg:inline-flex text-text-tertiary hover:text-text-primary p-1.5 rounded-md hover:bg-surface-1 transition-colors flex-shrink-0"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <PanelLeftOpen className="w-4 h-4" />
            ) : (
              <PanelLeftClose className="w-4 h-4" />
            )}
          </button>
          {/* Mobile close button */}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden inline-flex text-text-tertiary hover:text-text-primary p-1.5 rounded-md hover:bg-surface-1 transition-colors flex-shrink-0"
            aria-label="Close admin panel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="p-2 space-y-3 flex-1 overflow-y-auto">
          {/* Home link */}
          <NavLinkItem
            href="/home"
            icon={Home}
            label="Back to Home"
            collapsed={renderCollapsed}
            active={false}
          />

          {/* Sections */}
          {NAV_SECTIONS
            .filter((section) => hasRoleAccess(section.roles))
            .map((section) => {
              const visibleItems = section.items.filter((item) => hasRoleAccess(item.roles));
              if (visibleItems.length === 0) return null;
              const sectionIsCollapsed = sectionCollapsed[section.title];

              return (
                <div key={section.title} className="space-y-0.5">
                  {/* Section header — hidden in slim mode; in slim mode all items
                      flow inline so the section is implicit. */}
                  {!renderCollapsed && (
                    <button
                      type="button"
                      onClick={() => toggleSection(section.title)}
                      className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-medium text-text-tertiary uppercase tracking-wider hover:text-text-secondary transition-colors"
                    >
                      <span>{section.title}</span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform ${
                          sectionIsCollapsed ? '-rotate-90' : ''
                        }`}
                      />
                    </button>
                  )}

                  {/* In slim mode, always render items (sections are implicit).
                      In expanded mode, respect the per-section collapse state. */}
                  {(renderCollapsed || !sectionIsCollapsed) && (
                    <div className="space-y-0.5">
                      {visibleItems.map((item) => (
                        <NavLinkItem
                          key={item.href}
                          href={item.href}
                          icon={item.icon}
                          label={item.label}
                          collapsed={renderCollapsed}
                          active={isActivePath(item.href)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Visual divider between sections in slim mode */}
                  {renderCollapsed && (
                    <div className="my-2 border-t border-border" />
                  )}
                </div>
              );
            })}
        </nav>
      </aside>

      {/* Floating action button — only on mobile when the drawer is closed.
          Gated by JS (not just `lg:hidden`) so desktop users don't carry the
          dead button in their DOM, and so the test assertion stays meaningful. */}
      {isMobileDrawerClosed && (
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="lg:hidden fixed bottom-6 right-4 z-40 w-12 h-12 rounded-full bg-brand text-white shadow-lg hover:shadow-xl hover:bg-brand-hover active:scale-95 transition-[transform,box-shadow,background-color] duration-200 flex items-center justify-center"
          aria-label="Open admin panel"
          aria-expanded={mobileOpen}
          aria-controls="admin-sidebar"
        >
          <Settings className="w-5 h-5" />
        </button>
      )}
    </>
  );
};

interface NavLinkItemProps {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  collapsed: boolean;
  active: boolean;
}

const NavLinkItem: React.FC<NavLinkItemProps> = ({ href, icon: Icon, label, collapsed, active }) => {
  const baseClasses =
    'flex items-center rounded-md text-sm transition-colors';
  const layoutClasses = collapsed
    ? 'justify-center px-2 py-2'
    : 'gap-2.5 px-3 py-2';
  const stateClasses = active
    ? 'bg-brand-light text-brand font-medium' + (collapsed ? '' : ' border-l-2 border-brand')
    : 'text-text-secondary hover:bg-surface-1 hover:text-text-primary';

  return (
    <Link
      to={href}
      className={`${baseClasses} ${layoutClasses} ${stateClasses}`}
      // Native tooltip in slim mode so the user can still see the label.
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
      {!collapsed && active && (
        <ChevronRight className="w-3.5 h-3.5 ml-auto flex-shrink-0" />
      )}
    </Link>
  );
};

export { AdminSidebar };
